import { useEffect, useMemo, useState } from "react";

import { usePreviewHistoryQuery } from "@/api/previewHistory";
import { usePreviewRunTurnsQuery } from "@/api/previewReplay";
import { useCapability } from "@/auth/SessionContext";
import { TEXT_READ_GAP_MS } from "@/lib/labThrottle";
import { backgroundEndTurn } from "@/lib/previewReplay";

import { LabContinueFromTurn } from "./LabContinueFromTurn";

/** How many recent runs Skip BG looks through for one that got past it. */
const SCAN_LIMIT = 5;

/**
 * BG mode "Skip BG": the start from the bar. It takes the most recent run of this
 * project that got past background and replays its patient lines up to where
 * background ended. The runs are read one at a time, a gap apart (upstream Liv
 * allows ~10 requests per 10 s). The patient is the source run's when this
 * browser started it; otherwise the form opens and asks.
 */
export function LabSkipBackground({
  projectId,
  candidateId,
  onStarted,
}: {
  projectId: string;
  candidateId: string | null;
  onStarted: (previewRunRef: string) => void;
}) {
  const inspect = useCapability("preview.inspect");
  const history = usePreviewHistoryQuery(projectId, {
    enabled: inspect.allowed,
  });
  const runs = useMemo(
    () =>
      (history.data?.pages.flatMap((page) => page.items) ?? [])
        .filter((item) => item.inspectionState === "inspectable")
        .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
        .slice(0, SCAN_LIMIT),
    [history.data],
  );
  // The scan stops on the first run that got past background, so `index` rests on it.
  const [index, setIndex] = useState(0);
  const scanned = runs[index]?.previewRunRef ?? "";
  const turns = usePreviewRunTurnsQuery(projectId, scanned, {
    enabled: inspect.allowed && scanned !== "",
  });
  const settled = scanned !== "" && !turns.isPending;
  const end = turns.data?.events ? backgroundEndTurn(turns.data.events) : null;
  const found = end === null ? null : { runRef: scanned, end };

  useEffect(() => {
    if (!settled || end !== null) {
      return;
    }
    const timer = window.setTimeout(
      () => setIndex((current) => current + 1),
      TEXT_READ_GAP_MS,
    );
    return () => window.clearTimeout(timer);
  }, [settled, end, scanned]);

  if (!inspect.allowed) {
    return null;
  }
  if (history.isPending) {
    return (
      <p role="status" className="self-center text-xs text-muted-foreground">
        Looking for a recent run that got past background…
      </p>
    );
  }
  if (history.isError) {
    return (
      <p role="alert" className="self-center text-xs text-destructive">
        The earlier conversations could not be read, so Skip BG cannot start.
      </p>
    );
  }
  if (runs.length === 0) {
    return (
      <p className="self-center text-xs text-muted-foreground">
        Skip BG replays an earlier conversation that got past background; there
        is none yet.
      </p>
    );
  }
  if (found !== null) {
    return (
      <LabContinueFromTurn
        projectId={projectId}
        previewRunRef={found.runRef}
        turnNumber={found.end}
        candidateId={candidateId}
        onContinued={onStarted}
        label="New conversation"
        quick
        note={
          <>
            Replays the most recent run that got past background, through turn{" "}
            {found.end}, then waits for you. Liv answers every line afresh, so
            background may end at another turn this time.
          </>
        }
      />
    );
  }
  return index >= runs.length ? (
    <p className="self-center text-xs text-muted-foreground">
      No recent run got past background, so there is nothing to skip to yet.
    </p>
  ) : (
    <p role="status" className="self-center text-xs text-muted-foreground">
      Looking for a recent run that got past background…
    </p>
  );
}
